import {
  createApiHeaders,
  tenantAwareApiWrapper,
  withServiceClient,
} from './portal-http';

import { memoryClient } from '@terra/shared-rest-clients-memory';
import type { MemoryContract } from '@terra/shared-contracts-memory';
import { type MemoryRecordResponse } from '@terra/shared-contracts-memory';

export class EndpointMemoryCreateRejectedError extends Error {
  readonly rejectionReason: string;

  constructor(rejectionReason: string) {
    super(rejectionReason);
    this.name = 'EndpointMemoryCreateRejectedError';
    this.rejectionReason = rejectionReason;
  }
}

const extractRejectionReason = (body: unknown): string | undefined => {
  if (!body || typeof body !== 'object') {
    return undefined;
  }

  const record = body as Record<string, unknown>;
  const rejectionReason = record.rejectionReason;

  if (typeof rejectionReason === 'string' && rejectionReason.trim()) {
    return rejectionReason;
  }

  const message = record.message;

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return undefined;
};

export const portalMemoryApi = {
  getMemoriesByEndpoint: (
    applicationId: string,
    endpointId: string,
    query: {
      skip?: number;
      limit?: number;
      search?: string;
    } = {},
  ) =>
    tenantAwareApiWrapper<typeof MemoryContract.listEndpointMemoryRecords>({
      endpointFn: (headers) =>
        memoryClient.listEndpointMemoryRecords({
          params: { applicationId, endpointId },
          query,
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  createEndpointMemoryRecord: async (
    applicationId: string,
    endpointId: string,
    content: string,
  ) => {
    const headers = await createApiHeaders();
    const response = await memoryClient.createEndpointMemoryRecord({
      params: { applicationId, endpointId },
      body: { content, entryPoint: 'torch_endpoint' },
      ...withServiceClient(headers, 'ai'),
    });

    if (response.status === 201) {
      return response.body;
    }

    if (response.status === 400) {
      throw new EndpointMemoryCreateRejectedError(
        extractRejectionReason(response.body) ?? '',
      );
    }

    throw new Error(`API call failed with status: ${response.status}`);
  },

  deleteEndpointMemoryRecord: (
    applicationId: string,
    endpointId: string,
    recordId: string,
  ) =>
    tenantAwareApiWrapper<typeof MemoryContract.deleteEndpointMemoryRecord>({
      endpointFn: (headers) =>
        memoryClient.deleteEndpointMemoryRecord({
          params: { applicationId, endpointId, recordId },
          query: { entryPoint: 'torch_endpoint' },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  updateEndpointMemoryRecord: (
    applicationId: string,
    endpointId: string,
    recordId: string,
    content: string,
    sessionId?: string,
  ) =>
    tenantAwareApiWrapper<typeof MemoryContract.updateEndpointMemoryRecord>({
      endpointFn: (headers) =>
        memoryClient.updateEndpointMemoryRecord({
          params: { applicationId, endpointId, recordId },
          body: { content, sessionId, entryPoint: 'torch_endpoint' },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  getMemoriesByApplication: (applicationId: string) =>
    tenantAwareApiWrapper<typeof MemoryContract.listMemoryRecords>({
      endpointFn: async (headers) => {
        const pageSize = 250;
        let skip = 0;
        const records: MemoryRecordResponse[] = [];

        const firstResponse = await memoryClient.listMemoryRecords({
          params: { applicationId },
          query: { skip, limit: pageSize },
          ...withServiceClient(headers, 'ai'),
        });

        if (firstResponse.status !== 200) {
          return firstResponse;
        }

        let response = firstResponse;
        const toMappedRecords = (
          pageRecords: MemoryRecordResponse[],
        ): MemoryRecordResponse[] =>
          pageRecords.map((record) => ({
            ...record,
            strategy:
              record.strategy === 'wide_app' ? record.strategy : undefined,
          }));

        records.push(...toMappedRecords(response.body.records));
        skip = records.length;

        while (response.body.records.length >= pageSize) {
          const nextResponse = await memoryClient.listMemoryRecords({
            params: { applicationId },
            query: { skip, limit: pageSize },
            ...withServiceClient(headers, 'ai'),
          });

          if (nextResponse.status !== 200) {
            return nextResponse;
          }

          response = nextResponse;
          const pageRecords = toMappedRecords(response.body.records);

          records.push(...pageRecords);
          skip += pageRecords.length;
        }

        return {
          ...response,
          body: {
            records,
            totalCount: records.length,
          },
        };
      },
    }),

  createMemoryEvent: (
    applicationId: string,
    sessionId: string,
    messages: Array<{
      content: string;
      role: 'USER' | 'ASSISTANT' | 'TOOL' | 'OTHER';
    }>,
  ) =>
    tenantAwareApiWrapper<typeof MemoryContract.createMemoryEvent>({
      useBodyMessage: true,
      endpointFn: (headers) =>
        memoryClient.createMemoryEvent({
          params: { applicationId },
          body: {
            sessionId,
            messages,
            entityContext: [{ type: 'application', id: applicationId }],
          },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  updateMemoryRecord: (
    applicationId: string,
    recordId: string,
    content: string,
    sessionId?: string,
  ) =>
    tenantAwareApiWrapper<typeof MemoryContract.updateMemoryRecord>({
      useBodyMessage: true,
      endpointFn: (headers) =>
        memoryClient.updateMemoryRecord({
          params: { applicationId, recordId },
          body: { content, sessionId, entryPoint: 'torch_application' },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  deleteMemoryRecord: (applicationId: string, recordId: string) =>
    tenantAwareApiWrapper<typeof MemoryContract.deleteMemoryRecord>({
      endpointFn: (headers) =>
        memoryClient.deleteMemoryRecord({
          params: { applicationId, recordId },
          query: { entryPoint: 'torch_application' },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  /**
   * Creates a memory record directly (semantic) without going through conversational extraction.
   */

  createMemoryRecord: (applicationId: string, content: string) =>
    tenantAwareApiWrapper<typeof MemoryContract.createMemoryRecord>({
      endpointFn: (headers) =>
        memoryClient.createMemoryRecord({
          params: { applicationId },
          body: { content, entryPoint: 'torch_application' },
          ...withServiceClient(headers, 'ai'),
        }),
      useBodyMessage: true,
    }),

  /**
   * Lists user preference memory records for the current user within an application.
   * Only returns preferences belonging to the authenticated user.
   */

  listUserPreferenceRecords: (applicationId: string) =>
    tenantAwareApiWrapper<typeof MemoryContract.listUserPreferenceRecords>({
      endpointFn: (headers) =>
        memoryClient.listUserPreferenceRecords({
          params: { applicationId },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  /**
   * Creates a user preference memory record for the current user.
   */

  createUserPreferenceRecord: (
    applicationId: string,
    content: string,
    entryPoint: 'torch_preference' | 'torch_chat_preference',
  ) =>
    tenantAwareApiWrapper<typeof MemoryContract.createUserPreferenceRecord>({
      endpointFn: (headers) =>
        memoryClient.createUserPreferenceRecord({
          params: { applicationId },
          body: { content, entryPoint },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  /**
   * Deletes a user preference memory record for the current user.
   * Uses the user-scoped namespace for existence verification.
   */

  deleteUserPreferenceRecord: (applicationId: string, recordId: string) =>
    tenantAwareApiWrapper<typeof MemoryContract.deleteUserPreferenceRecord>({
      endpointFn: (headers) =>
        memoryClient.deleteUserPreferenceRecord({
          params: { applicationId, recordId },
          query: { entryPoint: 'torch_preference' },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  /**
   * Updates a user preference memory record for the current user.
   * Implemented as delete-then-create in the user-scoped namespace.
   */

  updateUserPreferenceRecord: (
    applicationId: string,
    recordId: string,
    content: string,
  ) =>
    tenantAwareApiWrapper<typeof MemoryContract.updateUserPreferenceRecord>({
      endpointFn: (headers) =>
        memoryClient.updateUserPreferenceRecord({
          params: { applicationId, recordId },
          body: { content, entryPoint: 'torch_preference' },
          ...withServiceClient(headers, 'ai'),
        }),
    }),
};
